import { CommonModule } from '@angular/common'
import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatSidenavModule } from '@angular/material/sidenav'
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { RouterModule } from '@angular/router'
import { NavigationComponent, NavigationGroup, NavigationItem } from '@saniup-webapps/shared/components'
import { InvoiceService } from '../intern/services/invoice.service'
import { VertragsmeldungenService } from '../intern/services/vertragsmeldungen.service'
import { DeadletterService } from '../intern/services/deadletter.service'
import { EkvService } from '../services/ekv.service'

@Component({
  imports: [
    RouterModule,
    NavigationComponent,
    CommonModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './internal-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './internal-navigation.component.scss',
})
export class InternalNavigationComponent {
  private readonly invoiceService = inject(InvoiceService)
  private readonly deadletterService = inject(DeadletterService)
  private readonly vertragsmeldungen = inject(VertragsmeldungenService)
  private readonly ekvService = inject(EkvService)

  readonly homeItem: NavigationItem = { label: 'Startseite', icon: 'home', route: '/internal/landing' }

  /**
   * Reihenfolge: Aufgaben · Stammdaten · Auswertung · Abrechnung · System.
   * Oben stehen die Arbeitslisten mit Badge (der tägliche Einstieg), ganz unten die Technik.
   */
  readonly links = computed<NavigationGroup>(() => ({
    items: [],
    sections: [
      {
        label: 'Aufgaben',
        items: [
          {
            label: 'EKV Rückläufer',
            route: 'ekv',
            icon: 'contract_edit',
            badge: this.ekvService.ekvSaniUpCount.hasValue() ? this.ekvService.ekvSaniUpCount.value().counter : undefined,
          },
          {
            label: 'Rechnungen',
            route: 'invoice',
            icon: 'money_bag',
            badge: this.invoiceService.openInvoiceCount.hasValue() ? this.invoiceService.openInvoiceCount.value().counter : undefined,
          },
          { label: 'Fehlende EK', route: 'fehlende-ek', icon: 'price_check' },
        ],
      },
      {
        label: 'Stammdaten',
        items: [
          { label: 'Leistungserbringer', route: 'leistungserbringer', icon: 'house' },
          { label: 'Verträge', route: 'vertrag', icon: 'contract' },
          { label: 'Kriterien', route: 'kriterien', icon: 'filter_alt' },
          { label: 'Produkte', route: 'produktupload', icon: 'inventory_2' },
          { label: 'Import', route: 'import', icon: 'move_to_inbox' },
        ],
      },
      {
        label: 'Auswertung',
        items: [
          { label: 'Finanzen', route: 'finanzen', icon: 'account_balance_wallet' },
          { label: 'Lieferlandkarte', route: 'lieferlandkarte', icon: 'map' },
          { label: 'Fehlzustellungen', route: 'zustellquote', icon: 'deployed_code_alert' },
        ],
      },
      {
        label: 'Abrechnung',
        items: [
          { label: 'Belege einlesen', route: 'absetzung/import', icon: 'document_scanner' },
          { label: 'Abrechnungsupload', route: 'pflegebox/abrechnungsupload', icon: 'upload_file' },
          { label: 'Unterlagen', route: 'unterlagen', icon: 'folder_shared' },
          { label: 'Vertragsmeldungen', route: 'vertragsmeldungen', icon: 'handshake', badge: this.vertragsmeldungen.neu() || undefined },
        ],
      },
      {
        label: 'System',
        items: [
          {
            label: 'Deadletter',
            route: 'deadletter',
            icon: 'error_outline',
            badge: this.deadletterService.openDeadletterCount.hasValue()
              ? this.deadletterService.openDeadletterCount.value().counter
              : undefined,
          },
        ],
      },
    ],
  }))
}
